import { useRef, useState } from 'react'

import { t, term } from '../i18n'
import { dayMonthYear } from '../lib/format'
import { useBackButton } from '../lib/navigation'
import { haptic } from '../lib/telegram'
import type { Photo } from '../lib/types'
import { RadioRows } from './fields'
import { Icon } from './Icon'
import { usePhotoUrl } from './PhotoImage'
import { SheetShell } from './Sheet'

function Layer({ id, clip }: { id: string; clip?: string }) {
  const url = usePhotoUrl(id)
  if (!url) return null
  return (
    <img
      src={url}
      alt=""
      className="pointer-events-none absolute inset-0 h-full w-full object-cover"
      style={clip ? { clipPath: clip } : undefined}
    />
  )
}

export function PhotoCompare({
  photos,
  initial,
  onClose,
}: {
  photos: Photo[]
  initial: Photo
  onClose: () => void
}) {
  const ordered = photos.toSorted((a, b) => a.takenAt.localeCompare(b.takenAt))
  const fallback = ordered.find((photo) => photo.id !== initial.id) ?? initial

  const [left, setLeft] = useState(fallback.takenAt <= initial.takenAt ? fallback.id : initial.id)
  const [right, setRight] = useState(fallback.takenAt <= initial.takenAt ? initial.id : fallback.id)
  const [position, setPosition] = useState(50)
  const [picking, setPicking] = useState<'left' | 'right'>()
  const frame = useRef<HTMLDivElement>(null)
  const dragging = useRef(false)

  useBackButton(onClose)

  const older = ordered.find((photo) => photo.id === left) ?? initial
  const newer = ordered.find((photo) => photo.id === right) ?? initial

  function move(clientX: number) {
    const box = frame.current?.getBoundingClientRect()
    if (!box) return
    setPosition(Math.min(100, Math.max(0, ((clientX - box.left) / box.width) * 100)))
  }

  function pick(id: string) {
    haptic.select()
    if (picking === 'left') setLeft(id === right ? left : id)
    if (picking === 'right') setRight(id === left ? right : id)
    setPicking(undefined)
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-viewfinder">
      <div
        ref={frame}
        className="relative min-h-0 flex-1 touch-none overflow-hidden"
        onPointerDown={(event) => {
          dragging.current = true
          event.currentTarget.setPointerCapture(event.pointerId)
          move(event.clientX)
        }}
        onPointerMove={(event) => {
          if (dragging.current) move(event.clientX)
        }}
        onPointerUp={() => (dragging.current = false)}
        onPointerCancel={() => (dragging.current = false)}
      >
        <Layer id={older.id} />
        <Layer id={newer.id} clip={`inset(0 0 0 ${position}%)`} />

        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 w-px bg-on-accent/70"
          style={{ left: `${position}%` }}
        />
        <span
          aria-hidden
          className="pointer-events-none absolute flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-capsule border border-nav bg-nav text-text shadow-float backdrop-blur-3"
          style={{ left: `${position}%`, top: '50%' }}
        >
          <Icon name="columns" size={17} />
        </span>

        <div
          onPointerDown={(event) => event.stopPropagation()}
          className="absolute inset-x-4 top-[calc(var(--spacing-safe-top)+16px)] flex items-center justify-between gap-2"
        >
          <button
            type="button"
            onClick={onClose}
            aria-label={t.photos.closeCompare}
            className="pressable flex h-tap w-tap items-center justify-center rounded-capsule border border-nav bg-nav text-text backdrop-blur-3"
          >
            <Icon name="x" size={18} />
          </button>
          <span className="flex h-tap items-center rounded-capsule border border-nav bg-nav px-[14px] text-hint text-text backdrop-blur-3">
            {term('angle', initial.angle)}
          </span>
        </div>
      </div>

      <div className="flex flex-none gap-2 rounded-t-sheet-matte border-t border-hairline-soft bg-sheet-matte px-[14px] pb-[calc(14px+var(--spacing-safe-bottom))] pt-[14px]">
        <button
          type="button"
          onClick={() => setPicking('left')}
          className="pressable flex min-h-tap flex-1 flex-col items-start justify-center rounded-nested border border-hairline bg-field px-[14px] text-left"
        >
          <span className="text-meta text-text-secondary">{t.photos.compareLeft}</span>
          <span className="text-row text-text">{dayMonthYear(older.takenAt.slice(0, 10))}</span>
        </button>
        <button
          type="button"
          onClick={() => setPicking('right')}
          className="pressable flex min-h-tap flex-1 flex-col items-start justify-center rounded-nested border border-hairline bg-field px-[14px] text-left"
        >
          <span className="text-meta text-text-secondary">{t.photos.compareRight}</span>
          <span className="text-row text-text">{dayMonthYear(newer.takenAt.slice(0, 10))}</span>
        </button>
      </div>

      {picking ? (
        <SheetShell
          title={picking === 'left' ? t.photos.compareLeftTitle : t.photos.compareRightTitle}
          onClose={() => setPicking(undefined)}
        >
          <div className="pb-2 pt-2">
            <RadioRows
              value={picking === 'left' ? left : right}
              onChange={pick}
              options={ordered
                .toReversed()
                .filter((photo) => photo.id !== (picking === 'left' ? right : left))
                .map((photo) => ({
                  value: photo.id,
                  label: dayMonthYear(photo.takenAt.slice(0, 10)),
                }))}
            />
          </div>
        </SheetShell>
      ) : null}
    </div>
  )
}
